import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { LoaderCircle, X } from "lucide-react";
import { RootState, useAppDispatch } from "../../../../redux/store";
import { DatetimeDetail, ScheduleV2, ShiftCardMobileProps, SwapEmployeeV2 } from "../../../../types";
import { updateSelectedSwapEmployees } from "../../../../redux/features/schedule/ScheduleSlice";
import { DateTimeUtil } from "../../../../utils";
import { toast } from "react-toastify";

interface Props {
    startDate: Date | null;
    endDate: Date | null;
    handleStartDateChange: (date: Date | null) => void;
    handleEndDateChange: (date: Date | null) => void;
    handleNameSearch: (name: string) => void;
    onSearch: () => void;
    onSuccess: () => void;
}

const SelectionStep: React.FC<Props> = ({
    startDate,
    endDate,
    handleStartDateChange,
    handleEndDateChange,
    handleNameSearch,
    onSearch,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const selectedShift = useSelector((state: RootState) => state.schedule.selectedScheduleForCannotWork) as ScheduleV2;
    const swappableEmployees = useSelector((state: RootState) => state.schedule.swappableEmployees);
    const fetchingEmployeeStatus = useSelector((state: RootState) => state.schedule.requestState) as string;
    const actionConsecutiveDays = useSelector((state: RootState) => state.schedule.actionConsecutiveDays);
    const getEffectiveActionConsecutiveDays = actionConsecutiveDays > 1 ? actionConsecutiveDays - 1 : undefined;

    const [selectedEmployees, setSelectedEmployees] = useState<Set<SwapEmployeeV2>>(new Set());
    const [searchValue, setSearchValue] = useState("");


    const groupedEmployees = useMemo(() => {
        const grouped = new Map<number, SwapEmployeeV2[]>();
        swappableEmployees.forEach((employee) => {
            if (!DateTimeUtil.isAfter(new Date().toISOString(), employee.shift_start_date)) {
                if (!grouped.has(Number(employee.emp_id!))) {
                    grouped.set(Number(employee.emp_id!), []);
                }
                grouped.get(Number(employee.emp_id!))?.push(employee);
            }
        });
        return grouped;
    }, [swappableEmployees]);

    useEffect(() => {
        dispatch(updateSelectedSwapEmployees(Array.from(selectedEmployees)));
    }, [selectedEmployees]);

    const isEmployeeSelected = useCallback(
        (employee: SwapEmployeeV2) => {
            return Array.from(selectedEmployees).some(
                (selectedEmployee) => selectedEmployee.schedule_id === employee.schedule_id
            );
        },
        [selectedEmployees]
    );

    const handleCheckboxChangeV2 = useCallback((employee: SwapEmployeeV2, isChecked: boolean) => {
        setSelectedEmployees((prev) => {
            const next = new Set(prev);
            if (isChecked) {
                if (!Array.from(next).some((e) => e.schedule_id === employee.schedule_id)) {
                    next.add(employee);
                }
            } else {
                next.forEach((e) => {
                    if (e.schedule_id === employee.schedule_id) {
                        next.delete(e);
                    }
                });
            }
            return next;
        });
    }, []);

    const ShiftCard: React.FC<ShiftCardMobileProps> = ({ startDetails, selectedShift, actionConsecutiveDays }) => (
        <div className="flex items-center gap-2 text-blue-600 text-sm mb-3">
            <span>
                {
                    DateTimeUtil.formatDate(selectedShift?.start_time, DateTimeUtil.MONTH_DATE)
                }
            </span>
            {getEffectiveActionConsecutiveDays && (
                <span className="text-blue-600">+ {getEffectiveActionConsecutiveDays} days</span>
            )}
            <span>
                {DateTimeUtil.formatTimeRangeUtc(selectedShift?.start_time, selectedShift?.end_time)}
            </span>
        </div>
    );

    const getDatetimeDetails = (dateTime: string): DatetimeDetail => {
        const date = new Date(dateTime);
        return {
            month: date.toLocaleString("default", { month: "long" }),
            monthNumber: date.getMonth(),
            day: date.toLocaleString("default", { day: "numeric" }),
            dayNumber: date.getDate(),
            year: date.getFullYear(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
            timezone: date.toTimeString().split(" ")[1],
        };
    };

    const startDetails = getDatetimeDetails(selectedShift?.start_time!);

    return (
        <div className="bg-white px-4 mt-4 md:w-1/2 md:mx-auto">
            <ShiftCard
                actionConsecutiveDays={actionConsecutiveDays}
                selectedShift={selectedShift}
                startDetails={startDetails}
            />

            <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-2">
                <div className="flex flex-col w-full sm:w-auto">
                    <label htmlFor="swap-start-date" className="text-xs text-gray-600 mb-1">Start date</label>
                    <input
                        id="swap-start-date"
                        type="date"
                        className="border rounded p-2 text-sm w-full sm:min-w-[180px]"
                        value={startDate ? DateTimeUtil.formatDateToMySQL(startDate, true) : ""}
                        onChange={(e) => handleStartDateChange(e.target.value ? new Date(e.target.value) : null)}
                        autoComplete="off"
                    />
                </div>
                <div className="flex flex-col w-full sm:w-auto">
                    <label htmlFor="swap-end-date" className="text-xs text-gray-600 mb-1">End date</label>
                    <input
                        id="swap-end-date"
                        type="date"
                        className="border rounded p-2 text-sm w-full sm:min-w-[180px]"
                        value={endDate ? DateTimeUtil.formatDateToMySQL(endDate, true) : ""}
                        onChange={(e) => handleEndDateChange(e.target.value ? new Date(e.target.value) : null)}
                        autoComplete="off"
                    />
                </div>
                <div className="flex flex-row items-end w-full sm:w-auto mb-2 sm:mb-0">
                    <div className="flex flex-col w-full">
                        <label htmlFor="swap-employee-search" className="text-xs text-gray-600 mb-1">Employee search</label>
                        <input
                            id="swap-employee-search"
                            type="text"
                            placeholder="Employee search..."
                            className="w-full border p-2 rounded text-sm"
                            value={searchValue}
                            onChange={(e) => {
                              handleNameSearch(e.target.value);
                              setSearchValue(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                onSearch();
                              }
                            }}
                            autoComplete="off"
                            aria-label="Search employees"
                        />
                    </div>
                    {searchValue && (
                        <button
                            className="ml-2 h-[38px] px-2 border rounded text-gray-600 hover:bg-gray-100"
                            aria-label="Clear search"
                            onClick={() => {
                                setSearchValue("");
                                handleNameSearch("");
                            }}
                        >
                            <X className="w-4 h-4 inline" />
                        </button>
                    )}
                </div>
                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm h-[38px]"
                    onClick={onSearch}
                >
                    Search
                </button>
            </div>

            <p className="text-sm mb-2">{selectedEmployees.size} Selected Covers:</p>

            {fetchingEmployeeStatus === "PENDING" ? (
                <div className="flex justify-center items-center">
                    <LoaderCircle className="animate-spin text-blue-500 w-6 h-6" />
                </div>
            ) : (
                <div className="space-y-3">
                    {Array.from(groupedEmployees).map(([empId, shifts]) => (
                        <div key={empId} className="border p-3 bg-gray-100 rounded">
                            <p className="text-sm font-medium mb-2">{shifts[0].employee_name}</p>
                            <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                {shifts.map((shift) => (
                                    <label key={shift.schedule_id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={isEmployeeSelected(shift)}
                                            onChange={(e) => handleCheckboxChangeV2(shift, e.target.checked)}
                                        />
                                        <span>
                                            {DateTimeUtil.formatDateTimeRangeUtc(
                                                `${shift.shift_start_date} ${shift.shift_start_time}`,
                                                `${shift.shift_end_date} ${shift.shift_end_time}`
                                            )}
                                            {getEffectiveActionConsecutiveDays
                                                ? ` + ${getEffectiveActionConsecutiveDays} days`
                                                : ""}
                                        </span>
                                        <span className="flex text-sm">({shifts[0].shift_name})</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {groupedEmployees.size > 0 && (
                <div className="text-right mt-4">
                    <button
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm"
                        onClick={() => {
                            if (selectedEmployees.size === 0) {
                                toast.error("Please select at least one employee to swap.");
                                return;
                            }
                            onSuccess();
                        }}
                    >
                        Continue
                    </button>
                </div>
            )}
        </div>
    );
};

export default SelectionStep;
